// services/podcastModeration.ts
// Podcast moderation service for reviewing and managing user-submitted podcast episodes

import {
    collection,
    query,
    where,
    getDocs,
    doc,
    updateDoc,
    addDoc,
    Timestamp,
    orderBy,
    limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
    PodcastEpisode,
    PodcastModerationQueueItem,
    PodcastModerationNotification,
    PodcastModerationStats,
    RejectionCategory,
    PodcastReviewStatus
} from '../types';

/**
 * Fetch moderation queue with pending podcast episodes
 */
export async function fetchPodcastModerationQueue(
    limitCount: number = 50,
    statusFilter?: PodcastReviewStatus
): Promise<PodcastModerationQueueItem[]> {
    try {
        // Query episodes from soundsLab/projects/items
        let episodesQuery = query(
            collection(db, 'soundsLab', 'projects', 'items')
        );

        // Apply status filter (default to pending)
        const status = statusFilter || 'pending';
        episodesQuery = query(
            episodesQuery,
            where('reviewStatus', '==', status),
            where('isDraft', '==', false),
            orderBy('submittedForReviewAt', 'desc'),
            firestoreLimit(limitCount)
        );

        const snapshot = await getDocs(episodesQuery);
        const queueItems: PodcastModerationQueueItem[] = [];

        for (const docSnap of snapshot.docs) {
            const data = docSnap.data();

            // Convert Firestore timestamps to Date objects
            const episode: PodcastEpisode = {
                id: docSnap.id,
                creatorProfileID: data.creatorProfileID || '',
                parentAppleID: data.parentAppleID || '',
                creatorName: data.creatorName || 'Unknown',
                creatorAvatarURL: data.creatorAvatarURL || null,
                title: data.title || 'Untitled',
                description: data.description || '',
                category: data.category || 'Other',
                coverArtURL: data.coverArtURL || null,
                duration: data.duration || 0,
                estimatedDuration: data.estimatedDuration || null,
                isDraft: data.isDraft || false,
                reviewStatus: data.reviewStatus || 'draft',
                isPublished: data.isPublished || false,
                publishedAt: data.publishedAt?.toDate() || null,
                sharedToBarkBoard: data.sharedToBarkBoard || false,
                submittedForReviewAt: data.submittedForReviewAt?.toDate() || null,
                reviewedAt: data.reviewedAt?.toDate() || null,
                reviewedBy: data.reviewedBy || null,
                rejectionReason: data.rejectionReason || null,
                rejectionCategory: data.rejectionCategory || null,
                removedAt: data.removedAt?.toDate() || null,
                removalReason: data.removalReason || null,
                flaggedBy: data.flaggedBy || null,
                flagReason: data.flagReason || null,
                submissionCount: data.submissionCount || 0,
                previousRejections: data.previousRejections || [],
                finalAudioURL: data.finalAudioURL || null,
                playCount: data.playCount || 0,
                averageListenDuration: data.averageListenDuration || 0,
                completionRate: data.completionRate || 0,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
                segments: data.segments || [],
                ttsSegments: data.ttsSegments || [],
                soundEffects: data.soundEffects || [],
                backgroundMusic: data.backgroundMusic || null,
                projectData: data.projectData || null
            };

            // Fetch user stats for this creator
            const userStats = await fetchUserStats(episode.creatorProfileID);

            queueItems.push({
                episode,
                userStats
            });
        }

        return queueItems;
    } catch (error) {
        console.error('Error fetching podcast moderation queue:', error);
        throw error;
    }
}

/**
 * Fetch user statistics for a creator
 */
export async function fetchUserStats(creatorProfileID: string) {
    try {
        const userEpisodesQuery = query(
            collection(db, 'soundsLab', 'projects', 'items'),
            where('creatorProfileID', '==', creatorProfileID),
            orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(userEpisodesQuery);

        let approvedCount = 0;
        let rejectedCount = 0;
        let removedCount = 0;
        let firstEpisodeDate: Date | undefined;

        snapshot.docs.forEach((doc) => {
            const data = doc.data();
            const status = data.reviewStatus;

            if (status === 'approved' || status === 'published') approvedCount++;
            if (status === 'rejected') rejectedCount++;
            if (status === 'removed') removedCount++;

            const createdAt = data.createdAt?.toDate();
            if (createdAt && (!firstEpisodeDate || createdAt < firstEpisodeDate)) {
                firstEpisodeDate = createdAt;
            }
        });

        const totalReviewed = approvedCount + rejectedCount;
        const approvalRate = totalReviewed > 0 ? approvedCount / totalReviewed : 0;

        return {
            totalEpisodes: snapshot.size,
            approvedCount,
            rejectedCount,
            removedCount,
            approvalRate,
            firstEpisodeDate
        };
    } catch (error) {
        console.error('Error fetching user stats:', error);
        return undefined;
    }
}

/**
 * Approve a podcast episode
 */
export async function approveEpisode(
    episodeID: string,
    adminEmail: string
): Promise<void> {
    try {
        // Get episode data to extract creatorProfileID and parentAppleID
        const episodeDoc = await getDocs(query(
            collection(db, 'soundsLab', 'projects', 'items'),
            where('__name__', '==', episodeID)
        ));

        if (episodeDoc.empty) {
            throw new Error('Episode not found');
        }

        const episodeData = episodeDoc.docs[0].data();
        const creatorProfileID = episodeData.creatorProfileID;
        const parentAppleID = episodeData.parentAppleID;
        const episodeTitle = episodeData.title || 'Your podcast';

        if (!parentAppleID || !creatorProfileID) {
            throw new Error('Episode missing required user identifiers (parentAppleID or creatorProfileID)');
        }

        // Prepare approval data
        const approvalData = {
            reviewStatus: 'approved',
            isPublished: true,
            publishedAt: Timestamp.now(),
            reviewedAt: Timestamp.now(),
            reviewedBy: adminEmail,
            rejectionReason: null,
            rejectionCategory: null,
            isDraft: false
        };

        // 1. Update user's recording path (where the real-time listener watches)
        const userRecordingRef = doc(
            db,
            'appleUsers',
            parentAppleID,
            'profiles',
            creatorProfileID,
            'recordings',
            episodeID
        );
        await updateDoc(userRecordingRef, approvalData);

        // 2. Update moderation queue path (for admin tracking)
        const moderationQueueRef = doc(db, 'soundsLab', 'projects', 'items', episodeID);
        await updateDoc(moderationQueueRef, approvalData);

        // Create approval notification
        const notification: Omit<PodcastModerationNotification, 'id'> = {
            type: 'podcast_approved',
            title: '🎉 Podcast Approved!',
            message: `Your podcast "${episodeTitle}" has been approved and is now live!`,
            episodeID: episodeID,
            rejectionReason: null,
            timestamp: new Date(),
            isRead: false,
            actionType: 'view'
        };

        await createNotification(creatorProfileID, notification);

    } catch (error) {
        console.error('Error approving episode:', error);
        throw error;
    }
}

/**
 * Reject a podcast episode with feedback
 */
export async function rejectEpisode(
    episodeID: string,
    adminEmail: string,
    rejectionReason: string,
    rejectionCategory: RejectionCategory
): Promise<void> {
    try {
        // Get episode data
        const episodeDoc = await getDocs(query(
            collection(db, 'soundsLab', 'projects', 'items'),
            where('__name__', '==', episodeID)
        ));

        if (episodeDoc.empty) {
            throw new Error('Episode not found');
        }

        const episodeData = episodeDoc.docs[0].data();
        const creatorProfileID = episodeData.creatorProfileID;
        const parentAppleID = episodeData.parentAppleID;
        const episodeTitle = episodeData.title || 'Your podcast';

        if (!parentAppleID || !creatorProfileID) {
            throw new Error('Episode missing required user identifiers (parentAppleID or creatorProfileID)');
        }

        // Track previous rejection if this is a resubmission
        const previousRejections = episodeData.previousRejections || [];
        if (episodeData.reviewStatus === 'rejected' && episodeData.rejectionReason) {
            previousRejections.push({
                rejectedAt: episodeData.reviewedAt?.toDate() || new Date(),
                rejectionReason: episodeData.rejectionReason,
                rejectionCategory: episodeData.rejectionCategory || 'other',
                reviewedBy: episodeData.reviewedBy || 'unknown'
            });
        }

        // Prepare rejection data
        const rejectionData = {
            reviewStatus: 'rejected',
            isPublished: false,
            reviewedAt: Timestamp.now(),
            reviewedBy: adminEmail,
            rejectionReason,
            rejectionCategory,
            previousRejections,
            isDraft: false
        };

        // 1. Update user's recording path (where the real-time listener watches)
        const userRecordingRef = doc(
            db,
            'appleUsers',
            parentAppleID,
            'profiles',
            creatorProfileID,
            'recordings',
            episodeID
        );
        await updateDoc(userRecordingRef, rejectionData);

        // 2. Update moderation queue path (for admin tracking)
        const moderationQueueRef = doc(db, 'soundsLab', 'projects', 'items', episodeID);
        await updateDoc(moderationQueueRef, rejectionData);

        // Create rejection notification
        const notification: Omit<PodcastModerationNotification, 'id'> = {
            type: 'podcast_rejected',
            title: 'Changes Needed',
            message: `Your podcast "${episodeTitle}" needs some updates before it can go live.`,
            episodeID: episodeID,
            rejectionReason: rejectionReason,
            timestamp: new Date(),
            isRead: false,
            actionType: 'edit'
        };

        await createNotification(creatorProfileID, notification);

    } catch (error) {
        console.error('Error rejecting episode:', error);
        throw error;
    }
}

/**
 * Remove a podcast episode for severe violations
 */
export async function removeEpisode(
    episodeID: string,
    adminEmail: string,
    removalReason: string
): Promise<void> {
    try {
        // Get episode data
        const episodeDoc = await getDocs(query(
            collection(db, 'soundsLab', 'projects', 'items'),
            where('__name__', '==', episodeID)
        ));

        if (episodeDoc.empty) {
            throw new Error('Episode not found');
        }

        const episodeData = episodeDoc.docs[0].data();
        const creatorProfileID = episodeData.creatorProfileID;
        const parentAppleID = episodeData.parentAppleID;

        if (!parentAppleID || !creatorProfileID) {
            throw new Error('Episode missing required user identifiers (parentAppleID or creatorProfileID)');
        }

        // Prepare removal data (soft delete - keep for audit trail)
        const removalData = {
            reviewStatus: 'removed',
            isPublished: false,
            reviewedAt: Timestamp.now(),
            reviewedBy: adminEmail,
            removedAt: Timestamp.now(),
            removalReason,
            isDraft: false
        };

        // 1. Update user's recording path (where the real-time listener watches)
        const userRecordingRef = doc(
            db,
            'appleUsers',
            parentAppleID,
            'profiles',
            creatorProfileID,
            'recordings',
            episodeID
        );
        await updateDoc(userRecordingRef, removalData);

        // 2. Update moderation queue path (for admin tracking)
        const moderationQueueRef = doc(db, 'soundsLab', 'projects', 'items', episodeID);
        await updateDoc(moderationQueueRef, removalData);

        // Create removal notification (generic message for safety)
        const notification: Omit<PodcastModerationNotification, 'id'> = {
            type: 'podcast_removed',
            title: 'Podcast Removed',
            message: 'Your podcast was removed for violating community guidelines.',
            episodeID: episodeID,
            rejectionReason: removalReason,
            timestamp: new Date(),
            isRead: false,
            actionType: null
        };

        await createNotification(creatorProfileID, notification);

        // Log moderation action for audit trail
        await logModerationAction({
            type: 'podcast_removed',
            episodeID,
            creatorProfileID,
            adminEmail,
            reason: removalReason
        });

    } catch (error) {
        console.error('Error removing episode:', error);
        throw error;
    }
}

/**
 * Flag an episode for senior review
 */
export async function flagForSeniorReview(
    episodeID: string,
    adminEmail: string,
    flagReason: string
): Promise<void> {
    try {
        // Get episode data
        const episodeDoc = await getDocs(query(
            collection(db, 'soundsLab', 'projects', 'items'),
            where('__name__', '==', episodeID)
        ));

        if (episodeDoc.empty) {
            throw new Error('Episode not found');
        }

        const episodeData = episodeDoc.docs[0].data();
        const creatorProfileID = episodeData.creatorProfileID;
        const parentAppleID = episodeData.parentAppleID;

        if (!parentAppleID || !creatorProfileID) {
            throw new Error('Episode missing required user identifiers (parentAppleID or creatorProfileID)');
        }

        // Prepare flag data
        const flagData = {
            reviewStatus: 'flagged',
            flaggedBy: adminEmail,
            flagReason,
            reviewedAt: Timestamp.now(),
            isDraft: false
        };

        // 1. Update user's recording path (where the real-time listener watches)
        const userRecordingRef = doc(
            db,
            'appleUsers',
            parentAppleID,
            'profiles',
            creatorProfileID,
            'recordings',
            episodeID
        );
        await updateDoc(userRecordingRef, flagData);

        // 2. Update moderation queue path (for admin tracking)
        const moderationQueueRef = doc(db, 'soundsLab', 'projects', 'items', episodeID);
        await updateDoc(moderationQueueRef, flagData);

    } catch (error) {
        console.error('Error flagging episode:', error);
        throw error;
    }
}

/**
 * Create a notification for the user
 */
async function createNotification(
    creatorProfileID: string,
    notification: Omit<PodcastModerationNotification, 'id'>
): Promise<void> {
    try {
        const notificationsRef = collection(
            db,
            'notifications',
            creatorProfileID,
            'inbox'
        );

        await addDoc(notificationsRef, {
            type: notification.type,
            title: notification.title,
            message: notification.message,
            episodeID: notification.episodeID,
            rejectionReason: notification.rejectionReason,
            timestamp: Timestamp.fromDate(notification.timestamp),
            isRead: notification.isRead,
            actionType: notification.actionType
        });

    } catch (error) {
        console.error('Error creating notification:', error);
        throw error;
    }
}

/**
 * Log moderation action for audit trail
 */
async function logModerationAction(action: {
    type: string;
    episodeID: string;
    creatorProfileID: string;
    adminEmail: string;
    reason: string;
}): Promise<void> {
    try {
        const logsRef = collection(db, 'podcast_moderation_logs');

        await addDoc(logsRef, {
            type: action.type,
            episodeID: action.episodeID,
            creatorProfileID: action.creatorProfileID,
            adminEmail: action.adminEmail,
            reason: action.reason,
            timestamp: Timestamp.now()
        });

    } catch (error) {
        console.error('Error logging moderation action:', error);
        // Don't throw - logging failure shouldn't block the main action
    }
}

/**
 * Get podcast moderation statistics
 */
export async function getPodcastModerationStats(): Promise<PodcastModerationStats> {
    try {
        // Get pending episodes
        const pendingQuery = query(
            collection(db, 'soundsLab', 'projects', 'items'),
            where('reviewStatus', '==', 'pending'),
            where('isDraft', '==', false)
        );
        const pendingSnapshot = await getDocs(pendingQuery);

        // Calculate backlog (episodes older than 24 hours)
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        let backlogCount = 0;
        pendingSnapshot.docs.forEach(doc => {
            const submittedAt = doc.data().submittedForReviewAt?.toDate();
            if (submittedAt && submittedAt < twentyFourHoursAgo) {
                backlogCount++;
            }
        });

        // Get episodes reviewed today, this week, this month
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        const allReviewedQuery = query(
            collection(db, 'soundsLab', 'projects', 'items'),
            where('reviewedAt', '!=', null)
        );
        const allReviewedSnapshot = await getDocs(allReviewedQuery);

        let reviewedToday = 0;
        let reviewedThisWeek = 0;
        let reviewedThisMonth = 0;
        let approvedCount = 0;
        let totalReviewed = 0;
        const rejectionReasons: { [key: string]: number } = {};
        const episodesByCategory: { [key: string]: number } = {};

        allReviewedSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const reviewedAt = data.reviewedAt?.toDate();
            const reviewStatus = data.reviewStatus;

            if (reviewedAt) {
                if (reviewedAt >= todayStart) reviewedToday++;
                if (reviewedAt >= weekStart) reviewedThisWeek++;
                if (reviewedAt >= monthStart) reviewedThisMonth++;

                if (reviewStatus === 'approved' || reviewStatus === 'published') {
                    approvedCount++;
                    totalReviewed++;
                } else if (reviewStatus === 'rejected' || reviewStatus === 'removed') {
                    totalReviewed++;
                }

                // Track rejection reasons
                if (reviewStatus === 'rejected' && data.rejectionCategory) {
                    rejectionReasons[data.rejectionCategory] =
                        (rejectionReasons[data.rejectionCategory] || 0) + 1;
                }

                // Track episodes by category
                if (data.category) {
                    episodesByCategory[data.category] =
                        (episodesByCategory[data.category] || 0) + 1;
                }
            }
        });

        const approvalRate = totalReviewed > 0 ? (approvedCount / totalReviewed) * 100 : 0;

        return {
            totalPending: pendingSnapshot.size,
            totalReviewedToday: reviewedToday,
            totalReviewedThisWeek: reviewedThisWeek,
            totalReviewedThisMonth: reviewedThisMonth,
            approvalRate: Math.round(approvalRate),
            averageReviewTimeMinutes: 0, // TODO: Calculate from submission to review time
            queueBacklog: backlogCount,
            rejectionReasons,
            episodesByCategory
        };

    } catch (error) {
        console.error('Error fetching podcast moderation stats:', error);
        return {
            totalPending: 0,
            totalReviewedToday: 0,
            totalReviewedThisWeek: 0,
            totalReviewedThisMonth: 0,
            approvalRate: 0,
            averageReviewTimeMinutes: 0,
            queueBacklog: 0,
            rejectionReasons: {},
            episodesByCategory: {}
        };
    }
}

/**
 * Rejection reason templates
 */
export const rejectionTemplates: { [key in RejectionCategory]: string } = {
    inappropriate_language: "The [title/description/audio] contains language that isn't appropriate for our community. Please update it to be more kid-friendly.",

    off_topic_content: "This content doesn't match the selected category. Please either change the category or adjust your podcast content to match.",

    safety_concerns: "We noticed content that might make others uncomfortable. Please review our community guidelines and update your podcast.",

    quality_issues: "The audio quality is too low or contains excessive background noise. Please try recording in a quieter space or check your microphone.",

    metadata_issues: "Please add a more descriptive title and description so others can discover your amazing content!",

    other: "Your podcast needs some adjustments. Please review the feedback and make the necessary changes."
};

/**
 * Category display names and icons
 */
export const categoryInfo: { [key: string]: { name: string; icon: string } } = {
    "Story": { name: "Story", icon: "📖" },
    "Interview": { name: "Interview", icon: "🎤" },
    "Comedy": { name: "Comedy", icon: "😄" },
    "Educational": { name: "Educational", icon: "🎓" },
    "Music & Sounds": { name: "Music & Sounds", icon: "🎵" },
    "News & Current Events": { name: "News & Current Events", icon: "📰" },
    "Sports": { name: "Sports", icon: "⚽" },
    "Other": { name: "Other", icon: "✨" }
};
